import { z } from 'zod';

export const doc = <T extends z.ZodTypeAny>(schema: T, description: string): T => schema.describe(description);

/**
 * Marks a field as deprecated in OpenAPI (and for the API changelog).
 * Description must start with "Deprecated." so generators can set `deprecated: true`.
 */
export const deprecatedDoc = <T extends z.ZodTypeAny>(
    schema: T,
    useInstead: string,
): T => schema.describe(`Deprecated. Use \`${useInstead}\` instead.`);

export const enumValues = <T extends readonly [string, ...string[]]>(values: T): string => values.join(', ');

/**
 * String enum for OpenAPI. Description must not list the values — Zudoku already renders the enum.
 * Prefer this for large / grid column enums.
 */
export const stringEnum = <T extends readonly [string, ...string[]]>(
    values: T,
    description: string,
): z.ZodEnum<Writeable<T>> => z.enum(values).describe(description);

/**
 * String enum with Allowed values in the description (small coded fields where the meaning of
 * each code is explained in the same sentence). Prefer {@link stringEnum} when the enum list alone
 * is enough (e.g. grid columns).
 */
export const documentedStringEnum = <T extends readonly [string, ...string[]]>(
    values: T,
    description: string,
): z.ZodEnum<Writeable<T>> =>
    z.enum(values).describe(`${description} Allowed values: ${enumValues(values)}.`);

export const moneyField = (): z.ZodOptional<z.ZodNullable<z.ZodNumber>> =>
    z.number().nullish().describe('Money value as a JSON number.');

export const itemCountField = (minimum = 1): z.ZodNumber =>
    z.number().int().min(minimum).describe('Item count. Whole number.');

/**
 * Optional ISO code (e.g. country alpha-2 or currency alpha-3): exactly `length` letters,
 * or empty string / null / omitted. Uses refine instead of `z.union([...])` so OpenAPI stays a
 * single nullable string (Zudoku does not render a confusing anyOf Decision Table).
 */
export const optionalIsoCodeField = (
    length: number,
    description: string,
): z.ZodOptional<z.ZodNullable<z.ZodEffects<z.ZodString, string, string>>> =>
    doc(
        z
            .string()
            .refine((v) => v.length === 0 || v.length === length, {
                message: `Must be empty or exactly ${length} characters`,
            })
            .nullish(),
        description,
    );

/** Exactly `length` letters, or null / omitted (empty string not allowed — matches Joi `.length(n).allow(null)`). */
export const optionalExactLengthStringField = (
    length: number,
    description: string,
): z.ZodOptional<z.ZodNullable<z.ZodString>> =>
    doc(z.string().length(length).nullish(), description);

export const isoDateTimeField = (description = 'ISO 8601 date/time string.'): z.ZodOptional<z.ZodNullable<z.ZodString>> =>
    z.string().nullish().describe(description);

/** Timestamp when the resource entered a given status (ISO 8601). */
export const statusTimestampField = (statusName: string): z.ZodOptional<z.ZodNullable<z.ZodString>> =>
    isoDateTimeField(`Timestamp when the resource entered status \`${statusName}\` (ISO 8601).`);

export const optionalDocumentedStringEnum = <T extends readonly [string, ...string[]]>(
    values: T,
    description: string,
): z.ZodOptional<z.ZodNullable<z.ZodEnum<Writeable<T>>>> =>
    documentedStringEnum(values, description).nullish();

export const optionalDocumentedIntEnum = <T extends readonly [number, ...number[]]>(
    values: T,
    description: string,
): z.ZodOptional<z.ZodEffects<z.ZodNumber, number, number>> =>
    z.number().int().refine((v) => (values as readonly number[]).includes(v), {
        message: `Allowed values: ${values.join(', ')}`,
    }).optional().describe(`${description} Allowed values: ${values.join(', ')}.`);

type Writeable<T> = { -readonly [K in keyof T]: T[K] };
